import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ChevronLeft, ChevronRight, Phone, Settings } from 'lucide-react';
import { ActionMenu, Button, type ActionMenuItem } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { usePropertyNeighbours, type PropertyNeighbour } from './usePropertyNeighbours';

interface Props {
  propertyId: string;
  /** Der Reiter, auf dem der Nutzer steht — der Sprung landet auf demselben beim Nachbarn. */
  activeTabTo: string;
  canEdit: boolean;
  showManualBooking: boolean;
  onManualBooking: () => void;
  actions: ActionMenuItem[];
}

/**
 * Der Meta-Nav-Strip über der Apartmentakte: Rückweg, Apartmentwechsel und die Aktionen.
 *
 * <p>Steht nach §4 in einer eigenen Datei — `ApartmentDetailLayout` trug mit 353 Zeilen ohnehin
 * mehr, als eine Datei tragen soll.</p>
 */
export function ApartmentNavStrip({
  propertyId,
  activeTabTo,
  canEdit,
  showManualBooking,
  onManualBooking,
  actions,
}: Props) {
  const navigate = useNavigate();
  const neighbours = usePropertyNeighbours(propertyId);

  const go = (target: PropertyNeighbour) =>
    void navigate({
      // Derselbe Reiter beim Nachbarn: von „Preise & Belegung" führt der Pfeil nicht zurück auf
      // die Übersicht. Die Route kommt aus der Reiterliste, bleibt also typsicher.
      to: activeTabTo,
      params: { id: target.id, propertyId: target.id },
      search: target.search as never,
    });

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-1">
        {/* Der Rückweg führt in dieselbe gefilterte Seite zurück, aus der der Nutzer kam. */}
        <Link
          to="/properties"
          search={neighbours.listSearch as never}
          className="text-ink-soft hover:text-ink inline-flex min-h-11 items-center gap-1.5 text-[13px] font-medium transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={2} aria-hidden />
          Alle Apartments
        </Link>

        {neighbours.inList && (
          <span className="border-line/60 ml-1 flex items-center gap-0.5 border-l pl-1">
            <NeighbourButton
              direction="previous"
              target={neighbours.previous}
              onGo={go}
              loading={neighbours.loading}
            />
            <NeighbourButton
              direction="next"
              target={neighbours.next}
              onGo={go}
              loading={neighbours.loading}
            />
          </span>
        )}
      </div>

      {canEdit && (
        <div className="flex items-center gap-1">
          {showManualBooking && (
            <Button
              variant="brand"
              size="sm"
              leftIcon={<Phone size={13} strokeWidth={2} aria-hidden />}
              onClick={onManualBooking}
            >
              Neue Buchung
            </Button>
          )}
          <ActionMenu
            trigger={
              <Button variant="ghost" size="sm" aria-label="Weitere Aktionen" className="px-2">
                <Settings size={16} strokeWidth={2} aria-hidden />
              </Button>
            }
            items={actions}
          />
        </div>
      )}
    </div>
  );
}

function NeighbourButton({
  direction,
  target,
  onGo,
  loading,
}: {
  direction: 'previous' | 'next';
  target: PropertyNeighbour | null;
  onGo: (target: PropertyNeighbour) => void;
  loading: boolean;
}) {
  const isPrevious = direction === 'previous';
  const Icon = isPrevious ? ChevronLeft : ChevronRight;
  const edge = isPrevious ? 'Erstes Apartment der Liste' : 'Letztes Apartment der Liste';
  // Ein Sprung ist nie blind: der Name des Ziels steht als Beschriftung und als Kurzhinweis.
  const label = target
    ? `${isPrevious ? 'Vorheriges' : 'Nächstes'} Apartment: ${target.name}`
    : loading
      ? 'Nachbarn werden geladen'
      : edge;

  return (
    <button
      type="button"
      disabled={!target}
      onClick={() => target && onGo(target)}
      aria-label={label}
      title={label}
      className={cn(
        'focus-visible:ring-sage inline-flex size-11 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2',
        target ? 'text-ink-soft hover:bg-card-alt hover:text-ink' : 'text-ink-faint',
      )}
    >
      <Icon size={16} strokeWidth={2} aria-hidden />
    </button>
  );
}
